import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PollIcon from '@mui/icons-material/Poll';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/common/reducers/hooks/store.hook';
import { makeSelectNameForDisplay } from './redux/TeamInboxSelectors';
import { getQuotedInfo, getSenderColor } from './team-inbox-helpers';
import { useMessageScroll } from './message-scroll-context';

interface QuotedMessageBlockProps {
    /** The reply message that carries the quote reference. */
    message: any;
}

const mediaIconFor = (type: string) => {
    const sx = { fontSize: 15, marginInlineEnd: '3px', verticalAlign: 'text-bottom' as const };
    switch (type) {
        case 'image': return <PhotoCameraIcon sx={sx} />;
        case 'video':
        case 'gif': return <VideocamIcon sx={sx} />;
        case 'audio':
        case 'ptt': return <MicIcon sx={sx} />;
        case 'document': return <InsertDriveFileIcon sx={sx} />;
        case 'sticker': return <EmojiEmotionsIcon sx={sx} />;
        case 'location': return <LocationOnIcon sx={sx} />;
        case 'vcard':
        case 'multi_vcard': return <PersonIcon sx={sx} />;
        case 'poll_creation': return <PollIcon sx={sx} />;
        default: return null;
    }
};

/**
 * WhatsApp-style reply/quote block, rendered INSIDE the bubble above the body:
 * a rounded tinted block with a 4px colored left bar, a colored author line, and
 * a one-line ellipsized snippet (text, or a media-type label with a small icon).
 * Clicking jumps to the original message in the loaded window and flashes it.
 */
const QuotedMessageBlock: React.FC<QuotedMessageBlockProps> = ({ message }) => {
    const { t } = useTranslation();
    const { scrollToMessage } = useMessageScroll();

    const quoted = useMemo(() => getQuotedInfo(message), [message]);

    // Resolve the quoted author's display name (skip for our own quoted messages —
    // those render as "You"). Selector is memoized per author id.
    const selectAuthorName = useMemo(
        () => (quoted && !quoted.fromMe && quoted.authorId
            ? makeSelectNameForDisplay(quoted.authorId)
            : () => ''),
        [quoted?.fromMe, quoted?.authorId],
    );
    const resolvedAuthorName = useAppSelector(selectAuthorName);

    if (!quoted) return null;

    const authorLabel = quoted.fromMe
        ? t('you', { defaultValue: 'You' })
        : (resolvedAuthorName || t('teamInbox.quotedAuthorFallback', { defaultValue: '' }));
    const barColor = quoted.fromMe
        ? 'var(--wa-accent, #00a884)'
        : (getSenderColor(authorLabel || quoted.authorId || 'peer') || 'var(--wa-accent, #00a884)');

    const snippet = quoted.text?.trim() || quoted.mediaLabel || '';
    const showMediaIcon = !quoted.text?.trim() && !!quoted.mediaLabel;

    const handleClick = () => {
        if (quoted.shortId) scrollToMessage(quoted.shortId);
    };

    return (
        <Box
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                mb: '4px',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: quoted.shortId ? 'pointer' : 'default',
                backgroundColor: 'var(--wa-quote-bg, rgba(0,0,0,0.06))',
                minHeight: '38px',
                maxWidth: '100%',
            }}
        >
            {/* Colored left bar */}
            <Box sx={{ width: '4px', flexShrink: 0, backgroundColor: barColor }} />
            <Box sx={{ px: '8px', py: '4px', minWidth: 0, flex: 1 }}>
                {authorLabel && (
                    <Typography
                        component="div"
                        sx={{
                            fontSize: '12.5px',
                            fontWeight: 500,
                            lineHeight: '16px',
                            color: barColor,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {authorLabel}
                    </Typography>
                )}
                <Typography
                    component="div"
                    dir="auto"
                    sx={{
                        fontSize: '13px',
                        lineHeight: '18px',
                        color: 'var(--wa-meta-text, #667781)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {showMediaIcon && mediaIconFor(quoted.type)}
                    {snippet}
                </Typography>
            </Box>
        </Box>
    );
};

export default React.memo(QuotedMessageBlock);
